import {
  resetContractorBadgePicturesQuery,
  useContractorBadgePictures,
} from "@src/appV2/Agents/api/useContractorBadgePictures";
import { useGetVerificationSelfie } from "@src/appV2/Agents/api/useGetVerificationSelfie";
import {
  resetAgentProfilePictureQuery,
  useWorkerProfilePicture,
} from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { resetGetPictureForBadgeQuery, useGetPictureForBadge } from "./useGetPictureForBadge";

interface UseGetAllPossiblePicturesForBadgeProps {
  workerId: string;
  options?: {
    refetchOnWindowFocus?: boolean;
  };
}

export function useGetAllPossiblePicturesForBadge(props: UseGetAllPossiblePicturesForBadgeProps) {
  const { workerId, options } = props;
  const queryClient = useQueryClient();
  const {
    data: profilePicture,
    status: profilePictureStatus,
    isFetching: isFetchingProfilePicture,
  } = useWorkerProfilePicture(workerId, {
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
  });
  const { data: profilePictureBase64, status: profilePictureBase64Status } = useGetPictureForBadge(
    profilePicture?.distribution_url,
    {
      refetchOnWindowFocus: options?.refetchOnWindowFocus,
    }
  );

  const { data: verificationSelfieBase64, status: verificationSelfieBase64Status } =
    useGetVerificationSelfie(
      {
        filter: { workerId },
      },
      {
        refetchOnWindowFocus: options?.refetchOnWindowFocus,
      }
    );

  const {
    data: lastSavedBadgePicture,
    status: lastSavedBadgePictureStatus,
    isFetching: isFetchingLastSavedBadgePicture,
  } = useContractorBadgePictures(
    { workerId },
    {
      refetchOnWindowFocus: options?.refetchOnWindowFocus,
    }
  );
  const { data: lastSavedBadgePictureBase64, status: lastSavedBadgePictureBase64Status } =
    useGetPictureForBadge(lastSavedBadgePicture?.data?.attributes.presignedUrl, {
      refetchOnWindowFocus: options?.refetchOnWindowFocus,
    });

  return {
    profilePictureBase64,
    profilePictureBase64Status:
      profilePictureStatus === "success" ? profilePictureBase64Status : profilePictureStatus,
    verificationSelfieBase64,
    verificationSelfieBase64Status,
    lastSavedBadgePictureBase64,
    lastSavedBadgePictureBase64Status:
      lastSavedBadgePictureStatus === "success"
        ? lastSavedBadgePictureBase64Status
        : lastSavedBadgePictureStatus,
    resetQueries: useCallback(() => {
      resetAgentProfilePictureQuery(queryClient, { workerId });
      resetContractorBadgePicturesQuery(queryClient);
      resetGetPictureForBadgeQuery(queryClient);
    }, [queryClient, workerId]),
    isFetchingProfilePicture,
    isFetchingLastSavedBadgePicture,
  };
}
